import { getAppEnv } from './environment';

/**
 * Rutas posibles del menú lateral (referencia).
 * Usar estos valores en las listas HIDDEN / BLOCKED.
 */
export const MENU_ROUTES = {
  USERS: 'userlist',
  ROLES: 'rolelist',
  SUPPLIERS: 'Supplierslist',
  PAYMENT_SUPPLEMENTS: 'PaymentSupplementslist',
  NOTIFICATIONS: 'Notificationlist',
  DOCUMENTS: 'Documentslist',
  JOB_REPORT_MANAGER: 'JobReportManager',
};

/**
 * Grupo de rutas relacionadas por feature del menú.
 * Al bloquear la clave del menú, también se bloquean estas rutas hijas.
 */
const ROUTE_GROUPS = {
  [MENU_ROUTES.USERS]: ['userlist', 'userform', 'userformedit', 'userdetail'],
  [MENU_ROUTES.ROLES]: ['rolelist', 'roleform', 'roleformedit', 'roledetail'],
  [MENU_ROUTES.SUPPLIERS]: ['Supplierslist', 'Suppliersform', 'Suppliersformedit', 'Suppliersdetail'],
  [MENU_ROUTES.PAYMENT_SUPPLEMENTS]: [
    'PaymentSupplementslist',
    'PaymentSupplementsformedit',
    'PaymentSupplementsdetail',
  ],
  [MENU_ROUTES.NOTIFICATIONS]: [
    'Notificationlist',
    'Notificationform',
    'Notificationformedit',
    'Notificationdetail',
  ],
  [MENU_ROUTES.DOCUMENTS]: ['Documentslist', 'Documentsformedit', 'Documentsdetail'],
  [MENU_ROUTES.JOB_REPORT_MANAGER]: ['JobReportManager'],
};

/**
 * Solo oculta del menú. La ruta sigue siendo accesible si se conoce la URL.
 */
const HIDDEN_FROM_MENU_BY_ENV = {
  development: [],
  staging: [],
  production: [MENU_ROUTES.JOB_REPORT_MANAGER],
};

/**
 * Bloquea acceso real: no aparece en menú y el router redirige.
 * - Logueado  → /
 * - Sin sesión → /login 
 */
const BLOCKED_ROUTES_BY_ENV = {
  development: [],
  staging: [],
  production: [],
};

/**
 * Lista blanca opcional por environment (menú + acceso).
 * Si un env tiene entradas, SOLO esas features quedan disponibles.
 */
const VISIBLE_ONLY_BY_ENV = {
  // production: [MENU_ROUTES.DOCUMENTS, MENU_ROUTES.SUPPLIERS],
};

function getMenuKey(item) {
  return item.route || item.documentName || item.title;
}

function expandRouteKeys(keys = []) {
  const expanded = new Set();
  keys.forEach((key) => {
    expanded.add(key);
    const group = ROUTE_GROUPS[key];
    if (group) {
      group.forEach((routeName) => expanded.add(routeName));
    }
  });
  return expanded;
}

function getBlockedRouteNames() {
  const env = getAppEnv();
  const blocked = BLOCKED_ROUTES_BY_ENV[env] || [];
  const onlyVisible = VISIBLE_ONLY_BY_ENV[env];

  if (Array.isArray(onlyVisible) && onlyVisible.length > 0) {
    const allowed = expandRouteKeys(onlyVisible);
    const allKnown = expandRouteKeys(Object.values(MENU_ROUTES));
    const blockedByWhitelist = [...allKnown].filter((name) => !allowed.has(name));
    return expandRouteKeys([...blocked, ...blockedByWhitelist]);
  }

  return expandRouteKeys(blocked);
}

function getHiddenFromMenuRouteNames() {
  const env = getAppEnv();
  const hidden = HIDDEN_FROM_MENU_BY_ENV[env] || [];
  return expandRouteKeys([...hidden, ...(BLOCKED_ROUTES_BY_ENV[env] || [])]);
}

/**
 * Indica si un ítem del menú debe mostrarse en el environment actual.
 */
export function isMenuItemVisible(item) {
  const env = getAppEnv();
  const key = getMenuKey(item);

  if (!key) {
    return true;
  }

  const onlyVisible = VISIBLE_ONLY_BY_ENV[env];
  if (Array.isArray(onlyVisible) && onlyVisible.length > 0 && !onlyVisible.includes(key)) {
    return false;
  }

  return !getHiddenFromMenuRouteNames().has(key);
}

/**
 * Indica si una ruta del router es accesible en el environment actual.
 * @param {string} routeName - `to.name` de vue-router
 */
export function isRouteAccessible(routeName) {
  if (!routeName) {
    return true;
  }
  return !getBlockedRouteNames().has(routeName);
}

/**
 * Filtra una lista de ítems (y submenús) según las listas de visibilidad.
 */
export function filterMenuByEnvironment(items = []) {
  return items
    .map((item) => {
      if (!item.subMenus || !item.subMenus.length) {
        return item;
      }
      return {
        ...item,
        subMenus: filterMenuByEnvironment(item.subMenus),
      };
    })
    .filter((item) => {
      if (!isMenuItemVisible(item)) {
        return false;
      }
      if (item.subMenus) {
        return item.route || item.subMenus.length > 0;
      }
      return true;
    });
}

export function getBlockedRoutesForCurrentEnv() {
  return [...getBlockedRouteNames()];
}

export function getHiddenMenuRoutesForCurrentEnv() {
  return [...getHiddenFromMenuRouteNames()];
}
